import React, { useState } from 'react';
import './RegistryVerify.css';

const API = process.env.REACT_APP_API_URL || 'https://api.stratosstandard.com';

// The interactive lookup is gated off until the public GET /registry/{id}
// endpoint ships (TODO.md §12). The registry *messaging* still renders so zone
// 3's positioning holds; the tool just doesn't advertise a feature that can't
// return results yet. Flip to "true" in the build env once the backend is live.
const VERIFY_ENABLED = process.env.REACT_APP_REGISTRY_VERIFY === 'true';

/**
 * RegistryVerify — zone 3: the database & authority-verification block.
 *
 * The "permanent searchable database entry" made tangible. Anyone — a parent,
 * a grandparent, a future school — can look up a certificate ID and confirm it
 * is a genuine Stratos Standard registry entry. A public verifiable record is
 * what turns a certificate into authority, and it reinforces the
 * registry framing over any health/assessment framing.
 *
 * Calls GET /registry/{id}; degrades gracefully if the endpoint isn't live yet.
 */
export default function RegistryVerify() {
  const [id, setId] = useState('');
  const [state, setState] = useState('idle'); // idle | loading | found | notfound | error
  const [record, setRecord] = useState(null);

  async function lookup(e) {
    e.preventDefault();
    const code = id.trim().toUpperCase();
    if (!code) return;
    setState('loading');
    setRecord(null);
    try {
      const res = await fetch(`${API}/registry/${encodeURIComponent(code)}`);
      if (res.status === 404) { setState('notfound'); return; }
      if (!res.ok) { setState('error'); return; }
      const data = await res.json();
      setRecord(data);
      setState('found');
    } catch (_) {
      setState('error');
    }
  }

  return (
    <section className="registry-verify" aria-labelledby="registry-title">
      <div className={`container registry-inner${VERIFY_ENABLED ? '' : ' registry-inner--solo'}`}>
        <div className="registry-copy">
          <p className="eyebrow" style={{ color: '#3ba3e8' }}>The Stratos Registry</p>
          <h2 id="registry-title">A permanent record, not just a certificate</h2>
          <p className="registry-lead">
            Every child verified through Stratos earns a numbered entry in the Stratos
            Standard Registry — a permanent, publicly verifiable record of what they
            achieved and when. Anyone can confirm a certificate is genuine in seconds.
          </p>
          <ul className="registry-points">
            <li>
              <strong>Permanent.</strong> The entry stays on record for life — no
              re-testing, no expiry.
            </li>
            <li>
              <strong>Verifiable.</strong> Schools, family, and programs can confirm
              authenticity from the certificate ID alone.
            </li>
            <li>
              <strong>Searchable.</strong> Each entry has its own shareable verification
              page.
            </li>
          </ul>
          {!VERIFY_ENABLED && (
            <p className="registry-soon">
              Public certificate lookup is launching soon — every session already records a
              permanent registry entry today.
            </p>
          )}
        </div>

        {VERIFY_ENABLED && (
        <div className="registry-tool">
          <div className="registry-card">
            <div className="registry-card-head">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#d4a017" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 12l2 2 4-4"/><circle cx="12" cy="12" r="10"/></svg>
              <span>Verify a Registry Entry</span>
            </div>
            <form className="registry-form" onSubmit={lookup}>
              <label htmlFor="registry-id" className="registry-label">Certificate ID</label>
              <div className="registry-row">
                <input
                  id="registry-id"
                  className="registry-input"
                  placeholder="e.g. SS-2026-0481"
                  value={id}
                  onChange={(e) => setId(e.target.value)}
                  autoComplete="off"
                  spellCheck="false"
                />
                <button type="submit" className="btn btn-primary" disabled={state === 'loading'}>
                  {state === 'loading' ? 'Checking…' : 'Verify'}
                </button>
              </div>
            </form>

            <div className="registry-result" aria-live="polite">
              {state === 'found' && record && (
                <div className="registry-found">
                  <span className="registry-badge">✓ Verified registry entry</span>
                  <dl>
                    <div><dt>Recipient</dt><dd>{record.recipient || record.childFirstName || '—'}</dd></div>
                    <div><dt>Achievement</dt><dd>{record.achievement || record.certName || '—'}</dd></div>
                    <div><dt>Recorded</dt><dd>{record.date || record.issuedOn || '—'}</dd></div>
                    <div><dt>Entry ID</dt><dd>{record.id || id.trim().toUpperCase()}</dd></div>
                  </dl>
                </div>
              )}
              {state === 'notfound' && (
                <p className="registry-msg registry-msg-warn">
                  No registry entry matches that ID. Double-check the certificate and try again.
                </p>
              )}
              {state === 'error' && (
                <p className="registry-msg registry-msg-warn">
                  Verification is briefly unavailable. Please try again shortly.
                </p>
              )}
              {state === 'idle' && (
                <p className="registry-msg">
                  Enter the ID printed on any Stratos Standard certificate to confirm it.
                </p>
              )}
            </div>
          </div>
        </div>
        )}
      </div>
    </section>
  );
}
